import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Sun, Sparkles, Feather, ShieldCheck, Heart, Leaf, BookOpen, Star, Truck, Timer } from "lucide-react";
import { Button } from "@/components/ui/button";
import CheckoutModal from "@/components/CheckoutModal";

const BOOK_COVER =
  "https://customer-assets.emergentagent.com/job_e145e373-41d2-4ee0-a473-c83cedbfda3d/artifacts/jhy9wakc_file_0000000067f07207a1bd2725cfc5d00c.png";

const BENEFITS = [
  { title: "आनंद", description: "आंतरिक शांति और खुशी का अनुभव", Icon: Sun },
  { title: "प्रेरणा", description: "सकारात्मक ऊर्जा और दैनिक उत्साह", Icon: Sparkles },
  { title: "साधना", description: "आध्यात्मिक प्रगति और नियमित ध्यान", Icon: Feather },
  { title: "ब्रह्मचर्य", description: "ऊर्जा संरक्षण और मानसिक बल", Icon: ShieldCheck },
  { title: "स्वास्थ्य", description: "शारीरिक और मानसिक आरोग्य", Icon: Heart },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: "easeOut" },
  }),
};

const pad = (n) => String(n).padStart(2, "0");

function useCountdown() {
  const [left, setLeft] = useState({ h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const end = new Date();
      end.setHours(23, 59, 59, 999);
      const diff = Math.max(0, end - now);
      setLeft({
        h: Math.floor(diff / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return left;
}

export default function BookLanding() {
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const { h, m, s } = useCountdown();

  const openCheckout = () => setCheckoutOpen(true);

  return (
    <main className="min-h-screen w-full overflow-x-hidden text-brandgreen-dark" data-testid="landing-page">
      {/* Top ribbon */}
      <div className="w-full bg-brandgreen text-center py-2.5 px-4">
        <p className="text-white text-sm md:text-base font-sans tracking-wide flex items-center justify-center gap-2">
          <Leaf className="w-4 h-4 text-brandgold" />
          प्रेमानंद जी महाराज के आदर्शों से प्रेरित
        </p>
      </div>

      {/* Limited-time offer countdown */}
      <div className="w-full bg-brandgold/15 border-y border-brandgold/30 py-2.5 px-4" data-testid="offer-countdown">
        <p className="font-sans text-sm md:text-base text-brandgreen-dark font-semibold flex items-center justify-center gap-2 flex-wrap">
          <Timer className="w-4 h-4 text-brandgreen" />
          सीमित समय ऑफ़र समाप्त होने में:
          <span className="inline-flex items-center gap-1 font-bold tabular-nums">
            <span className="bg-brandgreen text-white rounded px-1.5 py-0.5">{pad(h)}</span>:
            <span className="bg-brandgreen text-white rounded px-1.5 py-0.5">{pad(m)}</span>:
            <span className="bg-brandgreen text-white rounded px-1.5 py-0.5">{pad(s)}</span>
          </span>
        </p>
      </div>
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Book cover - first on mobile */}
          <motion.div
            className="order-1 lg:order-2 flex justify-center"
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="relative">
              <div className="absolute -inset-6 bg-brandgold/20 blur-3xl rounded-full" aria-hidden="true" />
              <img
                src={BOOK_COVER}
                alt="शुद्ध दिनचर्या – सुख और स्वास्थ्य पुस्तक का कवर"
                className="relative w-64 sm:w-80 lg:w-[26rem] rounded-lg shadow-2xl animate-float"
                data-testid="book-cover-image"
                loading="eager"
              />
            </div>
          </motion.div>

          {/* Text */}
          <div className="order-2 lg:order-1 text-center lg:text-left">
            <motion.p
              variants={fadeUp} initial="hidden" animate="show" custom={0}
              className="font-sans text-brandgold font-semibold tracking-widest uppercase text-sm mb-4 flex items-center justify-center lg:justify-start gap-2"
            >
              <BookOpen className="w-4 h-4" /> हिंदी आध्यात्मिक मार्गदर्शिका
            </motion.p>

            <motion.h1
              variants={fadeUp} initial="hidden" animate="show" custom={1}
              className="text-4xl sm:text-5xl lg:text-6xl font-normal leading-tight text-brandgreen-dark"
              data-testid="book-title"
            >
              शुद्ध दिनचर्या<br />
              <span className="text-brandgold">सुख और स्वास्थ्य</span>
            </motion.h1>

            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={2} className="gold-divider mx-auto lg:mx-0 my-6" />

            <motion.p
              variants={fadeUp} initial="hidden" animate="show" custom={2}
              className="font-sans text-lg text-brandgreen leading-relaxed max-w-xl mx-auto lg:mx-0"
              data-testid="book-tagline"
            >
              सुखी, स्वस्थ और अनुशासित जीवन की ओर एक प्रेरक मार्गदर्शिका
            </motion.p>

            {/* Price + CTA */}
            <motion.div
              variants={fadeUp} initial="hidden" animate="show" custom={3}
              className="mt-8 flex flex-col sm:flex-row items-center gap-6 justify-center lg:justify-start"
            >
              <div className="text-center sm:text-left">
                <p className="font-sans text-sm text-brandgreen/70 line-through">₹299</p>
                <p className="font-serif text-5xl md:text-6xl font-bold text-brandgreen-dark leading-none" data-testid="book-price">
                  ₹150 <span className="text-lg font-sans font-normal text-brandgreen/70">मात्र</span>
                </p>
              </div>

              <Button
                onClick={openCheckout}
                data-testid="buy-now-button"
                className="rounded-full bg-brandgold hover:bg-brandgold-hover text-brandgreen-dark font-sans font-bold text-lg px-10 py-6 shadow-lg hover:shadow-xl hover:scale-105 transition-transform"
              >
                अभी खरीदें
              </Button>
            </motion.div>

            <motion.div
              variants={fadeUp} initial="hidden" animate="show" custom={4}
              className="mt-6 flex items-center gap-3 justify-center lg:justify-start"
            >
              <div className="flex text-brandgold">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-brandgold" />
                ))}
              </div>
              <span className="font-sans text-sm text-brandgreen/80">15 से 75 वर्ष तक के हर आयु वर्ग के लिए</span>
            </motion.div>

            <motion.div
              variants={fadeUp} initial="hidden" animate="show" custom={5}
              className="mt-5 inline-flex items-center gap-2 bg-brandgreen/10 border border-brandgreen/20 rounded-full px-4 py-2"
              data-testid="free-delivery-badge"
            >
              <Truck className="w-5 h-5 text-brandgreen" />
              <span className="font-sans text-sm font-semibold text-brandgreen-dark">
                आज ऑर्डर करने पर मुफ़्त डिलीवरी!
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="text-3xl md:text-4xl text-brandgreen-dark">इस पुस्तक से क्या पाएँगे</h2>
          <div className="gold-divider mx-auto mt-4" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {BENEFITS.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              whileHover={{ y: -6 }}
              className="bg-white border border-brandgreen/15 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`benefit-card-${i}`}
            >
              <div className="w-14 h-14 rounded-xl bg-brandgreen/10 flex items-center justify-center mb-5">
                <b.Icon className="w-7 h-7 text-brandgreen" strokeWidth={1.8} />
              </div>
              <h3 className="text-2xl text-brandgreen-dark mb-2">{b.title}</h3>
              <p className="font-sans text-brandgreen/80 leading-relaxed">{b.description}</p>
            </motion.div>
          ))}

          {/* CTA card in grid */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-brandgreen rounded-2xl p-8 flex flex-col justify-center items-center text-center shadow-md"
          >
            <p className="font-serif text-4xl text-brandgold font-bold mb-1">₹150</p>
            <p className="font-sans text-white/90 mb-5">जीवन बदलने वाली पुस्तक</p>
            <Button
              onClick={openCheckout}
              data-testid="buy-now-button-benefits"
              className="rounded-full bg-brandgold hover:bg-brandgold-hover text-brandgreen-dark font-sans font-bold px-8 py-5 hover:scale-105 transition-transform"
            >
              अभी खरीदें
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Final CTA banner */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        <div className="bg-white border border-brandgold/40 rounded-3xl p-8 md:p-12 text-center shadow-sm">
          <h2 className="text-2xl md:text-3xl text-brandgreen-dark mb-3">
            जीवन को शुद्ध बनाएं, मन को आनंद से भरें
          </h2>
          <p className="font-sans text-brandgreen/80 mb-6 max-w-2xl mx-auto">
            और स्वास्थ्य को अपनाएं — आज ही अपनी प्रति सुरक्षित करें।
          </p>
          <Button
            onClick={openCheckout}
            data-testid="buy-now-button-footer"
            className="rounded-full bg-brandgold hover:bg-brandgold-hover text-brandgreen-dark font-sans font-bold text-lg px-12 py-6 shadow-lg hover:scale-105 transition-transform"
          >
            अभी खरीदें – केवल ₹150
          </Button>
        </div>
      </section>

      <footer className="bg-brandgreen-dark text-center py-6 px-4">
        <p className="font-sans text-white/70 text-sm">
          © {new Date().getFullYear()} शुद्ध दिनचर्या – सुख और स्वास्थ्य · सुरक्षित भुगतान Cashfree द्वारा
        </p>
      </footer>

      <CheckoutModal open={checkoutOpen} onOpenChange={setCheckoutOpen} />
    </main>
  );
}
